import {CustomMailFolderType} from "@twigsby/client-data";
import {
    AlertBanner,
    ButtonSubmit,
    ColourSolid,
    ContentBlock,
    ContentLoader,
    FlexGap,
    FormWrapperLabel,
    Modal,
    ModalType,
    Select,
    StackVertical,
    useTranslation
} from "@twigsby/frontend-framework";
import {Controller, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import React, {useEffect} from "react";
import {useMailbox} from "../../../Providers/NewMailboxProvider";

interface CreateCustomFolderModalType extends ModalType {

}

const CreateCustomFolderModal = (props: CreateCustomFolderModalType) => {
    const { t } = useTranslation();
    const { customFolderStatus, customFolderData, customFolderError, activeCustomFolderData, CreateCustomFolder, EditCustomFolder } = useMailbox();

    const {
        control,
        handleSubmit,
        reset,
        formState,
        setValue,
    } = useForm({
        defaultValues: {
            id: activeCustomFolderData?.id || '',
            description: activeCustomFolderData?.description || '',
            colour: activeCustomFolderData?.colour || ColourSolid.Secondary,
            parentFolderId: activeCustomFolderData?.parentFolderId || null,
        },
        mode: "onChange"
    });

    const UpdateInitialData = () => {
        setValue('id',activeCustomFolderData?.id || '');
        setValue('description', activeCustomFolderData?.description || '');
        setValue('colour', activeCustomFolderData?.colour);
        setValue('parentFolderId', activeCustomFolderData?.parentFolderId || null);
    }

    const AddFolder = (newFolder: CustomMailFolderType) => {
        CreateCustomFolder(newFolder);
        reset();
    }

    const EditFolder = (editFolder: CustomMailFolderType) => {
        EditCustomFolder(editFolder);
        reset();
    }

    useEffect(() => {
        reset();
        if(activeCustomFolderData) {
            UpdateInitialData();
        }
    }, [props.isOpen, activeCustomFolderData]);

    return (
        <Modal title={activeCustomFolderData ? t('customFolders.editFolder.title') : t('customFolders.newFolder.title')} isOpen={props.isOpen} closeCallback={() => props.closeCallback()}>
            <form onSubmit={handleSubmit((data: any) => activeCustomFolderData ? EditFolder(data) : AddFolder(data))}>
                <fieldset className={'flex-column flex-gap-md'}>
                    <Controller
                        control={control}
                        name="description"
                        rules={{ required: "This is a required field", minLength: { value: 2, message: 'Too short'}}}
                        render={({ field, fieldState }) =>
                            <FormWrapperLabel
                                label={t('customFolders.newFolder.labels.name')}
                                invalid={fieldState.invalid}
                                validationMessage={fieldState.error?.message}
                            >
                                <input
                                    type='text'
                                    defaultValue={field.value}
                                    aria-invalid={fieldState.invalid ? 'true' : 'false'}
                                    autoComplete={'off'}
                                    autoFocus={true}
                                    onChange={(e) => field.onChange(e.currentTarget.value)}
                                />
                            </FormWrapperLabel>
                        } />

                    <Controller
                        control={control}
                        name="colour"
                        rules={{ required: "This is a required field" }}
                        render={({ field }) =>
                            <FormWrapperLabel label={t('customFolders.newFolder.labels.colour')} hideValidationMessage={true}>
                                <div className={'flex-row flex-gap-md'}>
                                    <div className={`colour-block ${ColourSolid.Secondary} ${field.value === ColourSolid.Secondary ? 'colour-selected' : ''}`} onClick={() => field.onChange(ColourSolid.Secondary)} />
                                    <div className={`colour-block ${ColourSolid.Primary} ${field.value === ColourSolid.Primary ? 'colour-selected' : ''}`} onClick={() => field.onChange(ColourSolid.Primary)} />
                                    <div className={`colour-block ${ColourSolid.Warning} ${field.value === ColourSolid.Warning ? 'colour-selected' : ''}`} onClick={() => field.onChange(ColourSolid.Warning)} />
                                    <div className={`colour-block ${ColourSolid.Alert} ${field.value === ColourSolid.Alert ? 'colour-selected' : ''}`} onClick={() => field.onChange(ColourSolid.Alert)} />
                                    <div className={`colour-block ${ColourSolid.Success} ${field.value === ColourSolid.Success ? 'colour-selected' : ''}`} onClick={() => field.onChange(ColourSolid.Success)} />
                                </div>
                            </FormWrapperLabel>
                        }
                    />

                    {customFolderStatus === "loading" ? (
                        <ContentLoader />
                    ) : customFolderStatus === "error" ? (
                        <ContentBlock error={true} title={customFolderError?.name as string} content={customFolderError?.message} />
                    ) : (
                        <>
                            {activeCustomFolderData?.childFolders && (
                                <StackVertical gap={FlexGap.ExtraSmall}>
                                    <label>{t('customFolders.newFolder.labels.hasParent')}</label>
                                    <AlertBanner
                                        title={'Folders containing nested folders can not have their own parent'}
                                        colourMod={ColourSolid.Warning}
                                    />
                                </StackVertical>
                            )}

                            {!activeCustomFolderData?.childFolders && customFolderData && customFolderData.length > 0 && (
                                <Controller
                                    control={control}
                                    name="parentFolderId"
                                    render={({ field, fieldState }) =>
                                        <FormWrapperLabel
                                            label={t('customFolders.newFolder.labels.hasParent')}
                                            invalid={fieldState.invalid}
                                            validationMessage={fieldState.error?.message}
                                        >
                                            <Select
                                                name='parentFolderId'
                                                defaultValue={field.value}
                                                placeholder={t('customFolders.newFolder.controls.hasParentPlaceholder')}
                                                aria-invalid={fieldState.invalid ? 'true' : 'false'}
                                                disabled={activeCustomFolderData?.childFolders}
                                                onChange={(e: any) => {
                                                    field.onChange(e.currentTarget.value === '-1' ? null : parseInt(e.currentTarget.value))
                                                }}
                                            >
                                                {customFolderData.map((folder, index: number) =>
                                                    !folder.parentFolderId && !activeCustomFolderData?.childFolders && activeCustomFolderData?.id !== folder.id
                                                        ? <option key={`newFolderOption${index}`} value={folder.id}>{folder.description}</option>
                                                        : null
                                                )}
                                            </Select>
                                        </FormWrapperLabel>
                                    }
                                />
                            )}
                        </>
                    )}

                    <ButtonSubmit title={activeCustomFolderData ? t('actions.update') : t('actions.save')} fullWidth={true} colour={ColourSolid.Primary} disabled={!formState.isValid} />
                </fieldset>
            </form>

        </Modal>
    )
}

export default CreateCustomFolderModal
